// Backend-driven language resolution (EL-13870), the mobile app's rules (its
// src/Utils/locale/resolveLocale.ts + getLanguageOptions.ts, ADR 0014) plus one web-only step: the
// user's last language in this browser (`preferredLocale`), used when the account has none. Pure
// and free of import.meta, so Jest can load it (src/i18n/index.ts can't be).

// The business languages: the ones every build ships and the only ones the backend stores
// (User.locale, Building.locale / availableLocale - EL-13872). pl/uk are web-internal and never
// come from the backend.
export const BUSINESS_LANGUAGES = ['en-US', 'fr-CA', 'es-US'] as const

export type BusinessLanguage = (typeof BUSINESS_LANGUAGES)[number]

export const FLOOR_LANGUAGE: BusinessLanguage = 'en-US'

// Validated by membership, never by truthiness: user.locale is null for every legacy user and
// could hold a tag the backend no longer lists.
export const isBusinessLanguage = (value: unknown): value is BusinessLanguage =>
  BUSINESS_LANGUAGES.includes(value as BusinessLanguage)

// The building's language flags (Building model, EL-14100).
export type BuildingLocaleSettings = {
  // Whether the property has turned translations on at all.
  i18nAvailable?: boolean | null
  // The property's allowed subset of BUSINESS_LANGUAGES.
  availableLocale?: string[] | null
  // The property's default language.
  locale?: string | null
}

export type ResolveLanguageInput = {
  userLocale?: string | null
  // The user's last language in this browser while on a building with translations on (see
  // src/components/providers/user-locale-sync/preferred-language.ts). Covers users whose
  // user.locale is null: an Administrator (PUT /admin can't save it) and anyone who hasn't
  // picked yet, so switching between two translated buildings keeps their language instead of
  // jumping to each building's default.
  preferredLocale?: string | null
  // undefined = the building isn't loaded yet; null = the user has no building at all.
  building: BuildingLocaleSettings | null | undefined
  // What to keep while the building isn't loaded - the language already on screen (cached in
  // localStorage from the last session), so the UI doesn't flip to en-US and back.
  current: string
}

const availableLocales = (building: BuildingLocaleSettings) =>
  (Array.isArray(building.availableLocale) ? building.availableLocale : []).filter(
    isBusinessLanguage,
  )

// - building not loaded        → keep `current`
// - no building                → en-US
// - i18nAvailable !== true     → en-US (user.locale and building.locale are ignored)
// - i18nAvailable === true     → user.locale if supported AND in availableLocale
//                              → else preferredLocale if supported AND in availableLocale
//                              → else building.locale if supported → else en-US
export const resolveLanguage = ({
  userLocale,
  preferredLocale,
  building,
  current,
}: ResolveLanguageInput): string => {
  if (building === undefined) return current
  if (building === null || building.i18nAvailable !== true) return FLOOR_LANGUAGE

  const allowed = availableLocales(building)
  const isAllowed = (locale: unknown): locale is BusinessLanguage =>
    isBusinessLanguage(locale) && allowed.includes(locale)

  if (isAllowed(userLocale)) return userLocale
  if (isAllowed(preferredLocale)) return preferredLocale
  if (isBusinessLanguage(building.locale)) return building.locale
  return FLOOR_LANGUAGE
}

// Picker options: the property's chosen subset (availableLocale). When it hasn't configured a
// usable list, only its default language (building.locale if supported, else en-US) - never every
// language, which would expose ones the property never opted into. The current language is always
// included so the selected row never disappears from under the user.
export const getLanguageOptions = ({
  building,
  currentLanguage,
}: {
  building: BuildingLocaleSettings
  currentLanguage: string
}): string[] => {
  const available = availableLocales(building)
  const base: string[] =
    available.length > 0
      ? available
      : [isBusinessLanguage(building.locale) ? building.locale : FLOOR_LANGUAGE]

  return base.includes(currentLanguage) ? base : [currentLanguage, ...base]
}
